import { useEffect, useState } from 'react'
import { useForm, type UseFormRegisterReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { z } from 'zod'

import { apiFetch, formatApiError, useTaxonomy } from '../shared/api/client'
import { locationSchema } from '../shared/api/schemas'
import { scoreOptions } from '../shared/lib/score-options'

type CreateLocationFormInput = z.input<typeof locationSchema>
type CreateLocationFormValues = z.output<typeof locationSchema>

function CreateLocationScoreField({
  label,
  idPrefix,
  registration,
}: {
  label: string
  idPrefix: string
  registration: UseFormRegisterReturn
}) {
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="flex gap-2">
        {scoreOptions().map((score) => (
          <label key={`${idPrefix}-${score}`} className="flex-1 cursor-pointer">
            <input
              type="radio"
              value={score}
              {...registration}
              className="peer sr-only"
            />
            <div className="rounded-xl border border-slate-200 bg-slate-50 py-3 text-center text-sm font-bold text-slate-600 transition peer-checked:border-emerald-500 peer-checked:bg-emerald-50 peer-checked:text-emerald-700 active:scale-95">
              {score}
            </div>
          </label>
        ))}
      </div>
    </div>
  )
}

export function CreateLocationModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const warningTerms = useTaxonomy('warning')
  const [step, setStep] = useState(0)

  const form = useForm<CreateLocationFormInput, unknown, CreateLocationFormValues>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      title: '',
      short_description: '',
      description: '',
      latitude: 46.0569,
      longitude: 14.5058,
      region_code: '',
      privacy_level: 3,
      access_quality: 3,
      scenery_level: 4,
      warning_keys: [],
    },
  })

  const mutation = useMutation({
    mutationFn: async (values: CreateLocationFormValues) => {
      return apiFetch('/locations', {
        method: 'POST',
        body: JSON.stringify({
          title: values.title,
          short_description: values.short_description || '',
          description: values.description || '',
          latitude: values.latitude,
          longitude: values.longitude,
          region_code: values.region_code || '',
          attributes: {
            privacy_level: values.privacy_level,
            access_quality: values.access_quality,
            scenery_level: values.scenery_level,
            warnings: (warningTerms.data?.results ?? [])
              .filter((term) => values.warning_keys.includes(term.key))
              .map((term) => term.id),
          },
        }),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['locations'] })
      form.reset()
      setStep(0)
      onClose()
    },
  })

  useEffect(() => {
    if (!isOpen) {
      form.reset()
      setStep(0)
      mutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  const stepTitles = ['Osnove', 'Koordinate', 'Ocena lokacije']
  const canGoBack = step > 0
  const isLastStep = step === stepTitles.length - 1
  const selectedWarningKeys = form.watch('warning_keys') ?? []

  const nextStep = async () => {
    const fieldsByStep: Array<Array<keyof CreateLocationFormValues>> = [
      ['title', 'short_description', 'description', 'region_code'],
      ['latitude', 'longitude'],
      ['privacy_level', 'access_quality', 'scenery_level'],
    ]

    const isValid = await form.trigger(fieldsByStep[step])
    if (isValid) setStep((current) => Math.min(current + 1, stepTitles.length - 1))
  }

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-sm md:items-center md:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-location-title"
      onClick={onClose}
    >
      <div
        className="animate-slide-up flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-y-auto rounded-t-[28px] bg-white p-5 pb-safe shadow-2xl ring-1 ring-slate-200 md:animate-none md:rounded-[28px] md:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex w-full justify-center pb-1 pt-1 md:hidden">
          <div className="h-1.5 w-12 rounded-full bg-slate-200" />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">
              Submit flow
            </p>
            <h2 id="create-location-title" className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
              Dodaj novo lokacijo
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Korak {step + 1} / {stepTitles.length}: {stepTitles[step]}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Zapri dodajanje lokacije"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-6 flex gap-2">
          {stepTitles.map((title, index) => (
            <div key={title} className="flex-1">
              <div className={`h-2 rounded-full ${index <= step ? 'bg-emerald-600' : 'bg-slate-200'}`} />
              <p className="mt-2 text-xs font-medium text-slate-500">{title}</p>
            </div>
          ))}
        </div>

        <form className="mt-6 space-y-5" onSubmit={form.handleSubmit((values) => mutation.mutate(values))}>
          {step === 0 ? (
            <div className="grid gap-4">
              <label className="text-sm font-medium text-slate-700">
                Naslov
                <input {...form.register('title')} placeholder="Bohinj ob vodi" className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10" />
              </label>
              <label className="text-sm font-medium text-slate-700">
                Kratek opis
                <input {...form.register('short_description')} placeholder="Mirna jasa ob vodi" className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10" />
              </label>
              <label className="text-sm font-medium text-slate-700">
                Opis
                <textarea {...form.register('description')} rows={5} placeholder="Dostop, senca, voda, posebnosti lokacije." className="mt-2 w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10" />
              </label>
              <label className="text-sm font-medium text-slate-700">
                Regija
                <input {...form.register('region_code')} placeholder="gorenjska" className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10" />
              </label>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">
                Latitude
                <input {...form.register('latitude')} type="number" step="0.000001" className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10" />
              </label>
              <label className="text-sm font-medium text-slate-700">
                Longitude
                <input {...form.register('longitude')} type="number" step="0.000001" className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10" />
              </label>
              <div className="sm:col-span-2 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                Za zdaj vnesi koordinate ročno. Naslednji korak bo klik na zemljevid in reverse geocoding.
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-6">
              <CreateLocationScoreField
                label="Zasebnost (1 = javno, 5 = skrito)"
                idPrefix="privacy-level"
                registration={form.register('privacy_level')}
              />

              <CreateLocationScoreField
                label="Dostop (1 = plezanje, 5 = asfalt)"
                idPrefix="access-quality"
                registration={form.register('access_quality')}
              />

              <CreateLocationScoreField
                label="Razgled in narava (1 = slabo, 5 = čudovito)"
                idPrefix="scenery-level"
                registration={form.register('scenery_level')}
              />

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <p className="font-semibold text-slate-900">Razpoložljiva opozorila</p>
                <p className="mt-1">Izberi opozorila, ki jih je treba pokazati obiskovalcem.</p>

                {(warningTerms.data?.results ?? []).length ? (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {(warningTerms.data?.results ?? []).map((term) => {
                      const checked = selectedWarningKeys.includes(term.key)
                      return (
                        <label
                          key={term.id}
                          className={`flex items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                            checked ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
                          }`}
                        >
                          <input
                            type="checkbox"
                            value={term.key}
                            checked={checked}
                            onChange={(event) => {
                              const next = event.target.checked
                                ? [...selectedWarningKeys, term.key]
                                : selectedWarningKeys.filter((key) => key !== term.key)
                              form.setValue('warning_keys', next, {
                                shouldDirty: true,
                                shouldTouch: true,
                              })
                            }}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500"
                          />
                          <span>
                            <span className="block font-semibold text-slate-900">
                              {term.label}
                            </span>
                            <span className="mt-1 block text-xs text-slate-500">
                              {term.description || term.key}
                            </span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">Ni naloženih opozoril.</p>
                )}
              </div>
            </div>
          ) : null}

          {Object.keys(form.formState.errors).length ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {Object.entries(form.formState.errors).map(([field, error]) => (
                <p key={field}>{field}: {error?.message?.toString()}</p>
              ))}
            </div>
          ) : null}

          {mutation.isError ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {formatApiError(mutation.error)}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <div className="text-sm text-slate-500">
              Po oddaji bo lokacija šla v pending pregled in se nato pokazala v discovery feedu.
            </div>
            <div className="flex flex-wrap gap-2">
              {canGoBack ? (
                <button type="button" onClick={() => setStep((current) => Math.max(current - 1, 0))} className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                  Nazaj
                </button>
              ) : null}
              {!isLastStep ? (
                <button type="button" onClick={() => void nextStep()} className="rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800">
                  Naprej
                </button>
              ) : (
                <button type="submit" disabled={mutation.isPending} className="rounded-full bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60">
                  {mutation.isPending ? 'Oddajam…' : 'Oddaj lokacijo'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
